import axios from 'axios';
import Cookies from 'js-cookie';
import createAuthRefreshInterceptor from 'axios-auth-refresh';

class Api {
  constructor() {
    this.authURL = process.env.REACT_APP_API_AUTH_URL;

    axios.defaults.timeout = 3600000;
    axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL;

    const accessToken = localStorage.getItem('hcatkn');

    if (accessToken) {
      axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
    }

    createAuthRefreshInterceptor(axios, this.refreshAuth);
  }

  auth = async (email, password) => {
    return axios({
      method: 'post',
      url: this.authURL,
      auth: {
        username: process.env.REACT_APP_API_CLIENT_KEY,
        password: process.env.REACT_APP_API_CLIENT_SECRET,
      },
      data: `grant_type=password&username=${encodeURIComponent(
        email
      )}&password=${encodeURIComponent(password)}`,
    })
      .then((res) => {
        localStorage.setItem('hcatkn', res.data.access_token);
        localStorage.setItem('hcrtkn', res.data.refresh_token);
        axios.defaults.headers.Authorization = `Bearer ${res.data.access_token}`;

        return res;
      })
      .catch((err) => err);
  };

  precheck = async (email, password) => {
    return axios
      .post('/auth/precheck/', { email, password })
      .then((res) => res)
      .catch((err) => err.response);
  };

  refreshAuth = (failedRequest) => {
    const refreshToken = localStorage.getItem('hcrtkn');

    if (refreshToken) {
      return axios({
        method: 'post',
        url: this.authURL,
        data: `grant_type=refresh_token&client_id=${process.env.REACT_APP_API_CLIENT_KEY}&client_secret=${process.env.REACT_APP_API_CLIENT_SECRET}&refresh_token=${refreshToken}`,
      })
        .then((res) => {
          localStorage.setItem('hcatkn', res.data.access_token);
          localStorage.setItem('hcrtkn', res.data.refresh_token);
          failedRequest.response.config.headers.Authorization = `Bearer ${res.data.access_token}`;

          return Promise.resolve();
        })
        .catch(() => {
          localStorage.clear('hcatkn');
          localStorage.clear('hcrtkn');
          window.location.href = '/';
        });
    }

    const refreshOrthancToken = Cookies.get('refresh_token')
    if(refreshOrthancToken){
      return axios
      .get('/orthanc_token/refresh/', {
        withCredentials: true // Importante para armazenar o cookie
      })
      .then((res) => res.data)
      .catch((error) => error.response);
    }

    return false;
  };

  generateOrthancToken = () => {
    return axios
    .get('/orthanc_token/generate/', {
      withCredentials: true // Importante para armazenar o cookie
    })
    .then((res) => res.data)
    .catch((error) => error.response);
  }

  refreshOrthancToken = () => {
    return axios
    .get('/orthanc_token/refresh/', {
      withCredentials: true // Importante para armazenar o cookie
    })
    .then((res) => res)
    .catch((error) => error.response);
  }

  postSendEmail = (data) => {
    return axios
      .post('/send_suport_email/', data)
      .then((res) => res.data)
      .catch((error) => error.response);
  };

  getMeUser = () => {
    return axios
      .get('/me/')
      .then((res) => res.data)
      .catch(() => false);
  };

  patchMeUser = (data) => {
    return axios
      .patch('/me/', data)
      .then((res) => res)
      .catch((error) => error.response);
  };

  postRequestPasswordReset = (email) => {
    return axios
      .get('/request_password_reset/', { params: { email } })
      .then((res) => res)
      .catch((error) => error.response);
  };

  postConfirmPasswordReset = (data) => {
    return axios
      .post('/confirm_password_reset/', data)
      .then((res) => res.data)
      .catch((error) => error.response);
  };
  

  registerUser = (data) => {
    return axios
      .post('/user/', data)
      .then((res) => res)
      .catch((err) => err.response);
  };

  getUserType = (type) => {
    return axios
      .get(`/user/?type=${type}`)
      .then((res) => res)
      .catch((err) => err.response);
  };

  getUser = (id) => {
    return axios.get(`/user/${id}`).then((res) => res);
  };

  getSearchUser = (query) => {
    return axios
      .get(`/user/`, { params: query })
      .then((res) => res)
      .catch((err) => err.response);
  };

  getResponsibleThatAreNotAdminUser = (query) => {
    return axios
      .get(`/user/?not_admin=true`, { params: query })
      .then((res) => res);
  };

  deleteUser = (id) => {
    return axios
      .delete(`/user/${id}`)
      .then((res) => res)
      .catch((err) => err.response);
  };

  patchEditUser = (id, data) => {
    return axios
      .patch(`/user/${id}/`, data)
      .then((res) => res)
      .catch((err) => err.response);
  };

  getPatientsWithExam = (institutionId) => {
    return axios
      .get(`/patient/?has_exam=true&institution=${institutionId}`)
      .then((response) => response.data)
      .catch(() => {
        throw new Error('Failed to get patients with exam');
      });
  };

  getAllReports = () => {
    return axios
      .get('/report/')
      .then((response) => response.data)
      .catch(() => {
        throw new Error('Failed to get all reports');
      });
  };

  getAllReportMini = () => {
    return axios
      .get('/report/?mini=true')
      .then((response) => response.data)
      .catch(() => {
        throw new Error('Failed to get all report mini');
      });
  };

  getAllReportMiniByInstitution = (institutionId) => {
    return axios
      .get(`/report/?institution=${institutionId}&mini=true`)
      .then((response) => response.data)
      .catch(() => {
        throw new Error('Failed to get all report mini by institution');
      });
  };

  getReport = (id) => {
    return axios
      .get(`/report/${id}/`)
      .then((response) => response)
      .catch((error) => error.response);
  };

  getSimplifiedReport = (id) => {
    return axios
      .get(`/report/${id}/?simple=true`)
      .then((response) => response)
      .catch((error) => error.response);
  };

  deleteReport = (id) => {
    return axios.delete(`/report/${id}/`);
  };

  deleteExamAndReport = (id) => {
    return axios.delete(`/exam/${id}`);
  };

  getWebViewer = (study_id) => {
    return axios.get(`/viewer_url/${study_id}`);
  };

  getOHIFViewer = (study_id) => {
    return axios.get(`/ohif_url/${study_id}`);
  };

  getStoneViewer = (study_id) => {
    return axios.get(`/stone_url/${study_id}`);
  };

  getExam = () => {
    return axios.get('/exam/');
  };

  getExams = (query) => {
    return axios
      .get('/exam/', { params: query })
      .then((response) => response)
      .catch((error) => error.response);
  };

  getExamResume = () => {
    return axios.get('/exam_performance');
  };

  getExamResumeByInstitution = (institutionId) => {
    return axios
      .get(`/exam_performance/?institution=${institutionId}`)
      .then((response) => response.data)
      .catch(() => {
        throw new Error('Failed to get exam resume by institution');
      });
  };

  getNotifications = () => {
    return axios.get('/notification/');
  };

  patchViewNotification = (id) => {
    return axios
      .patch(`/notification/${id}/`, { viewed: true })
      .then((response) => response.data);
  };

  getJustifications = () => {
    return axios
      .get('/justification/')
      .then((response) => response.data)
      .catch(() => {
        throw new Error('Failed to get the justifications');
      });
  };

  getJustificationsByExamType = (type) => {
    return axios
      .get(`/justification/?exam_type=${type}`)
      .then((response) => response.data)
      .catch(() => {
        throw new Error('Failed to get the justifications');
      });
  };

  approveReport = (id, feedback) => {
    return axios
      .post(`accept_report/?report=${id}`, { accepted: true, feedback: feedback })  
      .then((response) => response)
      .catch((error) => error.response);
  };

  disapproveReport = (id) => {
    return axios
      .patch(`/report/${id}/`, { accepted: false })
      .then((response) => response.data)
      .catch(() => {
        throw new Error('Failed to disapprove the report');
      });
  };

    markMaskSentToOrthanc = (id, mask_type) => {
    return axios
      .patch(`/mask_sent_to_orthanc/?id=${id}`, { mask_type: mask_type })
      .then((response) => response.data)
      .catch(() => {
        throw new Error('Failed to u the report');
      });
  };

  submitReportFeedback = (data) => {
    return axios
      .post('/report_feedback/', data)
      .then((response) => response.data)
      .catch(() => {
        throw new Error('Failed to submit the reports feedback');
      });
  };

  putReportFeedback = (id, data) => {
    return axios
      .put(`/report_feedback/${id}/`, data)
      .then((response) => response.data)
      .catch(() => {
        throw new Error('Failed to put the reports feedback');
      });
  };

  deleteReportFeedback = (id) => {
    return axios
      .delete(`/report_feedback/${id}/`)
      .then((response) => response.data)
      .catch(() => {
        throw new Error('Failed to delete the reports feedback');
      });
  };

  getReportFeedback = () => {
    return axios
      .get(`/report_feedback/`)
      .then((response) => response.data)
      .catch(() => {
        throw new Error('Failed to get the reports feedback');
      });
  };


  uploadDicom = (data) => {
    return axios
      .post('/upload_dicom/', data)
      .then((response) => response.data)
      .catch(() => {
        throw new Error('Failed to upload the dicom files');
      });
  };

  uploadDicomOrthancFromSeries = (series_id, data) => {
    return axios
      .post(`/upload_dicom/?series_id=${series_id}`, data)
      .then((response) => response.data)
      .catch((error) => error.response);
  };


  uploadDicomOrthanc = (data) => {
    return axios
      .post('/upload_dicom/', data)
      .then((response) => response)
      .catch((error) => error.response);
  };

  removeTempImages = () => {
    return axios
      .post('/remove_temp_image_dir/')
      .then((response) => response)
      .catch((error) => error.response);
  };

  getDicomData = (data) => {
    return axios
      .post('/get_dicom_data/', data)
      .then((response) => response)
      .catch((error) => error.response);
  };

  uploadDicomExamFile = (formData) => {
    return axios
      .post('/exam_file/', formData)
      .then((response) => response.data);
  };

  getRequestJpeg = (ids) => {
    return axios
      .get(`/request_jpeg_processing?ids=${ids}`)
      .then((response) => response.data);
  };

  getUser = (id) => {
    return axios
      .get(`/user/${id}`)
      .then((response) => response.data)
      .catch(() => {
        throw new Error('Failed to get user');
      });
  };

  getDoctorUsers = () => {
    return axios
      .get('/user/?type=DOC')
      .then((response) => response)
      .catch((error) => error.response);
  };

  searchPatientsByName = (name) => {
    return axios
      .get(`search_patient/?name=${name}`)
      .then((response) => response.data)
      .catch(() => {
        throw new Error('Failed to get patient by name');
      });
  };

  getDicomJpegs = (ids) => {
    return axios
      .get(`/get_jpegs/?ids=${ids.join(',')}`)
      .then((response) => response.data)
      .catch(() => {
        throw new Error('Failed to get dicom jpegs');
      });
  };

  getDicomJpegById = (id) => {
    return axios
      .get(`/get_jpeg/?id=${id}`, { responseType: 'blob' })
      .then((response) => response.data)
      .catch((error) => error.response);
  };


  getGLTFById = (id) => {
    return axios
      .get(`/get_gltf/?id=${id}`, { responseType: 'blob' })
      .then((response) => response)
      .catch((error) => error?.response);
  };


  getPDFCNNById = (id) => {
    return axios
      .get(`/get_pdf_cnn/?id=${id}`, { responseType: 'blob' })
      .then((response) => response)
      .catch((error) => error?.response);
  };


  getPDFDensitometriaById = (id) => {
    return axios
      .get(`/get_pdf_densitometria/?id=${id}`, { responseType: 'blob' })
      .then((response) => response)
      .catch((error) => error?.response);
  };


  getMaskIaById = (id) => {
    return axios
      .get(`/get_mask_ia/?id=${id}`, { responseType: 'blob' })
      .then((response) => response)
      .catch((error) => error?.response);
  };


  getSegEnsembleById = (id) => {
    return axios
      .get(`/get_seg_ensemble/?id=${id}`, { responseType: 'blob' })
      .then((response) => response)
      .catch((error) => error?.response);
  };

  getSegEnsembleEditedById = (id, type, file = null) => {
    if (file) {
      // PUT request to upload blob
      const formData = new FormData();
      formData.append('file', file);
  
      return axios
        .put(`/get_seg_ensemble_edited/?id=${id}&type=${type}`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        })
        .then((response) => response)
        .catch((error) => error?.response);
    } else {
      // GET request to fetch data
      return axios
        .get(`/get_seg_ensemble_edited/?id=${id}`, { responseType: 'blob' })
        .then((response) => response)
        .catch((error) => error?.response);
    }
  };
  
  getCompressNiftiById = (id) => {
    return axios
      .get(`/get_nifti/?id=${id}`, { responseType: 'blob' })
      .then((response) => response)
      .catch((error) => error?.response);
  };

  getDCMById = (id) => {
    return axios
      .get(`/get_dcm/?id=${id}`, { responseType: 'blob' })
      .then((response) => response)
      .catch((error) => error?.response);
  };

  getDicomJpegByPath = (path) => {
    return axios
      .get(`/get_jpeg_pydicom/?path=${path}`, { responseType: 'blob' })
      .then((response) => response.data)
      .catch((error) => error.response);
  };

  getMp4Video = (id) => {
    return axios
      .get(`/get_jpeg/?id=${id}&3d=true`, { responseType: 'blob' })
      .then((response) => response.data)
      .catch((error) => error.response);
  };

  getInstitution = () => {
    return axios
      .get('/institution/?mine=true')
      .then((response) => response)
      .catch((error) => error?.response);
  };

  getInstitutionById = (id) => {
    return axios
      .get(`/institution/${id}`)
      .then((response) => response)
      .catch((error) => error?.response);
  };

  getNotHeadquartersInstitution = () => {
    return axios
      .get('/institution/?mine=true&is_headquarters=false')
      .then((response) => response)
      .catch((error) => error.response);
  };

  getUniqueInstitutionData = (id) => {
    return axios.get(`/institution/${id}`).then((response) => response);
  };

  getAllHeadQuarterList = () => {
    return axios
      .get(`/institution/?is_headquarters=true`)
      .then((response) => response.data)
      .catch(() => {
        throw new Error('Failed to get the institutions List');
      });
  };

  getAllInstitutionsList = (page) => {
    return axios
      .get(`/institution/?is_headquarters=false&page=${page || 0}`)
      .then((response) => response.data)
      .catch(() => {
        throw new Error('Failed to get the institutions List');
      });
  };

  getInstitutionsByQuery = (query) => {
    return axios
      .get('/institution/', { params: query })
      .then((response) => response)
      .catch((error) => error?.response);
  };

  editInstitution = (id, data) => {
    return axios
      .patch(`/institution/${id}/`, data)
      .then((response) => response);
  };

  patchInstitutionPlan = (data) => {
    return axios.patch(`/institution_plan/`, data).then((response) => response);
  };

  postAddNewInstitution = (data) => {
    return axios
      .post('/institution/', data)
      .then((response) => response)
      .catch((error) => error?.response);
  };

  deleteInstitution = (id) => {
    return axios
      .delete(`/institution/${id}`)
      .then((response) => response.data)
      .catch(() => {
        throw new Error('Failed to delete the institution');
      });
  };

  registerPatient = (data) => {
    return axios
      .post('/register_patient/', data)
      .then((response) => response.data)
      .catch(() => {
        throw new Error('Failed to register patient');
      });
  };

  submitExam = (data) => {
    return axios
      .post('/exam/', data)
      .then((response) => response.data)
      .catch(() => {
        throw new Error('Failed to submit the exam');
      });
  };

  finishRegistration = (data) => {
    return axios
      .post('/finish_registration/', data)
      .then((response) => response)
      .catch((error) => error.response);
  };

  postUpdateUserPermission = (data) => {
    return axios
      .post(`/update_user_permission/`, data)
      .then((response) => response)
      .catch((error) => error.response);
  };

  postInstitutionAndPermissionUser = (data) => {
    return axios
      .post(`/institutions_and_permissions/`, data)
      .then((response) => response)
      .catch((error) => error.response);
  };

  getPermissions = () => {
    return axios.get(`/permissions/`).then((response) => response);
  };

  getInstitutionsAndPermissions = () => {
    return axios
      .get(`/institutions_and_permissions/`)
      .then((response) => response);
  };

  getUserByInstitution = (institutionId, query) => {
    return axios
      .get('/user/', {
        params: { institution: institutionId, ...query },
      })
      .then((response) => response)
      .catch((error) => error.response);
  };

  removeOrthancSerie = (serieId) => {
    return axios
      .post('/remove_series_from_orthanc/', { series: serieId })
      .then((response) => response)
      .catch((error) => error?.response);
  };

  getAnnotationTypes = () => {
    return axios
      .get('/annotation_type/')
      .then((response) => response)
      .catch((error) => error?.response);
  };

  getReportAnnotation = (id) => {
    return axios
      .get(`/annotation/?report=${id}`)
      .then((response) => response)
      .catch((error) => error?.response);
  };

  submitAnnotation = (data) => {
    return axios
      .post('/annotation/', data)
      .then((response) => response)
      .catch((error) => error?.response);
  };

  updateAnnotation = (id, data) => {
    return axios
      .patch(`/annotation/${id}/`, data)
      .then((response) => response)
      .catch((error) => error?.response);
  };

  getReportsMiniByPatient = (patientId) => {
    return axios
      .get(`/report/?patient=${patientId}&mini=true`)
      .then((response) => response)
      .catch((error) => error.response);
  };

  getReportAllImages = (reportId) => {
    return axios
      .get(`/report/${reportId}/?all_images=true`)
      .then((response) => response)
      .catch((error) => error.response);
  };

  getReportMask = (reportId) => {
    return axios
      .get(`/report_mask/?report=${reportId}`)
      .then((response) => response)
      .catch((error) => error.response);
  };

  submitSegmentation = (data) => {
    return axios
      .post('/segmentation/', data)
      .then((response) => response)
      .catch((error) => error?.response);
  };

  updateSegmentation = (id, data) => {
    return axios
      .patch(`/segmentation/${id}/`, data)
      .then((response) => response)
      .catch((error) => error?.response);
  };

  updateResults = (id) => {
    return axios
      .post(`/request_exam_update/?exam=${id}`)
      .then((response) => response)
      .catch((error) => error?.response);
  };

  getReportSegmentation = (id) => {
    return axios
      .get(`/segmentation/?report=${id}`)
      .then((response) => response)
      .catch((error) => error?.response);
  };

  getVersion = () => {
    return axios
      .get('/version/')
      .then((response) => response)
      .catch((error) => error.response);
  };

  exportExamsToCSV = (institutionId) => {
    return axios
      .get(`/export_csv/?id=${institutionId}`, { responseType: 'blob' })
      .then((response) => response)
      .catch((error) => error?.response);
  };
}

export default new Api();
